import { simplifySqrtLatex } from "../../../../utils/sqrtUtils";
﻿import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import SqrtInput from "../../../../components/inputs/SqrtInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";

function parseRootInput(str) {
  const raw = str && typeof str === "object" ? (str.raw ?? str.value ?? "") : str;
  const cleaned = String(raw ?? "")
    .trim()
    .replace(",", ".")
    .replace(/[\u00A0\u202F\u200B]/g, "") // dziwne spacje
    .replace(/\s+/g, "");

  if (!cleaned) return null;

  // √n
  const rootMatch = cleaned.match(/^√(\d+(?:\.\d+)?)$/);
  if (rootMatch) return Math.sqrt(parseFloat(rootMatch[1]));

  // a√n
  const coeffRootMatch = cleaned.match(/^(-?\d+(?:\.\d+)?)√(\d+(?:\.\d+)?)$/);
  if (coeffRootMatch) {
    const a = parseFloat(coeffRootMatch[1]);
    const n = parseFloat(coeffRootMatch[2]);
    return a * Math.sqrt(n);
  }

  // liczba bez pierwiastka
  const val = parseFloat(cleaned);
  return isNaN(val) ? null : val;
}

const FRACTIONAL_CENTER_PROBABILITY = 0.25;
const IRRATIONAL_RADIUS_PROBABILITY = 0.25;
const NICE_RADII = Array.from({ length: 14 }, (_, index) => index + 2);

function getRandomInt(min, max, random = Math.random) {
  return Math.floor(random() * (max - min + 1)) + min;
}

function getRandomFraction(minNum, maxNum, minDen, maxDen, random = Math.random) {
  let num, den;
  do {
    num = getRandomInt(minNum, maxNum, random);
    den = getRandomInt(minDen, maxDen, random);
  } while (den === 0 || num % den === 0); // prawdziwy ułamek, nie całkowita

  return num / den;
}

function getNonSquareRadiusSquared(random = Math.random) {
  let value;
  do {
    value = getRandomInt(2, 225, random);
  } while (Number.isInteger(Math.sqrt(value)));
  return value;
}

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  const maxDen = 10;
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

export function generateTask({ random = Math.random } = {}) {
  const useFractionalCenter = random() < FRACTIONAL_CENTER_PROBABILITY;
  const useIrrationalRadius = random() < IRRATIONAL_RADIUS_PROBABILITY;
  const a = useFractionalCenter
    ? getRandomFraction(-24, 24, 2, 4, random)
    : getRandomInt(-8, 8, random);
  const b = useFractionalCenter
    ? getRandomFraction(-24, 24, 2, 4, random)
    : getRandomInt(-8, 8, random);

  const radius = NICE_RADII[getRandomInt(0, NICE_RADII.length - 1, random)];
  const rSquared = useIrrationalRadius
    ? getNonSquareRadiusSquared(random)
    : radius ** 2;
  const r = Math.sqrt(rSquared);

  return {
    type: "ReadCircleFromEquationTask",
    data: { a, b, r, rSquared },
    answer: {
      center: { x: a, y: b },
      radius: {
        parsed: r,
        raw: `√${rSquared}`,
      },
    },
  };
}

export function renderTask({ data }) {
  const { a, b, rSquared } = data;

  const xExpr =
    a === 0 ? "x" : `(x ${a > 0 ? "-" : "+"} ${formatMixedFractionLatex(Math.abs(a))})`;

  const yExpr =
    b === 0 ? "y" : `(y ${b > 0 ? "-" : "+"} ${formatMixedFractionLatex(Math.abs(b))})`;

  return (
    <div className="space-y-2">
      <p>Odczytaj współrzędne środka oraz promień okręgu opisanego równaniem:</p>
      <BlockMath math={`${xExpr}^2 + ${yExpr}^2 = ${rSquared}`} />
    </div>
  );
}

/** ✅ Input: 2× FractionInput dla środka + SqrtInput dla promienia */
export function renderInput(_, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={"x_S ="} /></span>
        <FractionInput
          value={ua?.x || ""}
          onChange={(val) => onChange({ ...ua, x: val })}
          disabled={disabled}
          label={"x_S"}
        />
      </div>

      <div className="flex items-center gap-2">
        <span><InlineMath math={"y_S ="} /></span>
        <FractionInput
          value={ua?.y || ""}
          onChange={(val) => onChange({ ...ua, y: val })}
          disabled={disabled}
          label={"y_S"}
        />
      </div>

      <div className="flex items-center gap-2">
        <span><InlineMath math={"r ="} /></span>
        <SqrtInput label={"r"}
          value={ua?.radius || ""}
          onChange={(valRaw) => {
            const raw = valRaw && typeof valRaw === "object" ? (valRaw.raw ?? "") : valRaw;
            const parsed = valRaw && typeof valRaw === "object" && typeof valRaw.parsed === "number"
              ? valRaw.parsed
              : parseRootInput(raw);
            onChange({
              ...ua,
              radius: { raw, parsed },
            });
          }}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ answer }, userAnswer) {
  const x = userAnswer?.x?.parsed;
  const y = userAnswer?.y?.parsed;
  const r = userAnswer?.radius?.parsed;

  const expectedX = answer.center.x;
  const expectedY = answer.center.y;
  const expectedR = answer.radius?.parsed || answer.radius;

  const roundTo = (v, digits = 4) => Math.round(v * 10 ** digits) / 10 ** digits;

  const close = (a, b) =>
    typeof a === "number" &&
    typeof b === "number" &&
    roundTo(a) === roundTo(b);

  return close(x, expectedX) && close(y, expectedY) && close(r, expectedR);
}

function getRadiusSquared(data, answer) {
  if (Number.isFinite(data?.rSquared)) return data.rSquared;
  const radius=answer.radius;
  const raw=typeof radius === "object" ? radius.raw : radius;
  const match=/^√(\d+)$/.exec(String(raw));
  if (match) return Number(match[1]);
  return Math.round((typeof radius === "object" ? radius.parsed : Number(radius)) ** 2);
}

export function renderAnswer({ answer, data }) {
  const x = answer.center.x;
  const y = answer.center.y;

  const xLatex = formatMixedFractionLatex(x);
  const yLatex = formatMixedFractionLatex(y);

  const rRaw = simplifySqrtLatex(getRadiusSquared(data, answer));

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`S = (${xLatex},\\ ${yLatex})`} />
      </div>
      <div>
        <InlineMath math={`r = ${rRaw}`} />
      </div>
    </div>
  );
}

export function renderExplanation({ answer, data }) {
  const { center, radius } = answer;
  const { x, y } = center;
  const radiusSquared = getRadiusSquared(data, answer);
  const rRaw = simplifySqrtLatex(radiusSquared);
  const r = typeof radius === "object" ? radius.parsed : radius;

  const xLatex = formatMixedFractionLatex(x);
  const yLatex = formatMixedFractionLatex(y);

  const formatSigned = (valLatex) => (valLatex.startsWith("-") ? `(${valLatex})` : valLatex);

  const xSigned = formatSigned(xLatex);
  const ySigned = formatSigned(yLatex);
  const graphCenter = { x: parseFloat(x), y: parseFloat(y) };
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [{ center: graphCenter, radius: r }],
    points: [graphCenter],
  });

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Równanie okręgu w postaci kanonicznej ma postać:
        <BlockMath math="(x - a)^2 + (y - b)^2 = r^2" />
        W naszym przypadku:
        <BlockMath
          math={`(x - ${xSigned})^2 + (y - ${ySigned})^2 = \\left(${rRaw}\\right)^2`}
        />
      </div>

      <div>
        <strong>Krok 2:</strong> Zatem środek ma współrzędne:
        <BlockMath math={`S = (${xLatex},\\ ${yLatex})`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Promień okręgu to:
        <BlockMath math={`r = \\sqrt{${radiusSquared}}${rRaw === `\\sqrt{${radiusSquared}}` ? "" : ` = ${rRaw}`}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Wizualizacja okręgu:
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <GraphViewer
            circles={[
              {
                center: graphCenter,
                radius: r,
                color: CIRCLE_GRAPH_COLORS.circle,
                label: "okrąg",
              },
            ]}
            points={{
              S: {
                x: graphCenter.x,
                y: graphCenter.y,
                color: CIRCLE_GRAPH_COLORS.point,
                size: 6,
              },
            }}
            xRange={xRange}
            yRange={yRange}
            width={graphSize}
            height={graphSize}
            showGrid
          />
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const xRaw = userAnswer?.x?.raw;
  const yRaw = userAnswer?.y?.raw;
  const rRaw = userAnswer?.radius?.raw || userAnswer?.radius;

  const valid =
    typeof xRaw === "string" &&
    typeof yRaw === "string" &&
    typeof rRaw === "string" &&
    xRaw.length > 0 &&
    yRaw.length > 0 &&
    rRaw.length > 0;

  if (!valid) return <i>(brak odpowiedzi)</i>;

  const xLatex = formatLatexPreview(xRaw, "x_S").split("=")[1];
  const yLatex = formatLatexPreview(yRaw, "y_S").split("=")[1];

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`S = (${xLatex},\\ ${yLatex})`} />
      </div>
      <div>
        <InlineMath math={`r = ${rRaw}`} />
      </div>
    </div>
  );
}
